import { ComponentProps, ReactNode } from 'react'
import { useMediaBreakpoint } from '@unbridaled/react-hooks'
import {
  Modal,
  ModalRoot,
  ModalPanel,
  ModalHeader,
  ModalContent,
  ModalTitle,
  ModalFooter,
  ModalTrigger,
  ModalDescription,
  ModalProps,
  ModalTitleProps,
  ModalFooterProps,
  ModalContentProps,
  ModalPanelProps,
  ModalRootProps,
  ModalClose
} from '../Modal'
import {
  BottomDrawer,
  BottomDrawerRoot,
  BottomDrawerNestedRoot,
  BottomDrawerPanel,
  BottomDrawerHeader,
  BottomDrawerContent,
  BottomDrawerTitle,
  BottomDrawerFooter,
  BottomDrawerTrigger,
  BottomDrawerDescription,
  BottomDrawerProps,
  BottomDrawerPanelProps,
  BottomDrawerClose
} from '../BottomDrawer'

export type ResponsiveModalProps = ModalProps & BottomDrawerProps
export const ResponsiveModal = ({
  // BottomDrawer specific props
  trigger,
  showHandle,
  isNested,
  shouldScaleBackground,
  repositionInputs,
  // Modal specific props
  size,
  ...props
}: ResponsiveModalProps): React.ReactNode => {
  const isDesktop = useMediaBreakpoint('sm')
  if (isDesktop) {
    return <Modal {...(size !== undefined ? { size } : {})} {...(trigger !== undefined ? { trigger } : {})} {...props} />
  }
  return (
    <BottomDrawer
      {...(shouldScaleBackground !== undefined ? { shouldScaleBackground } : {})}
      {...(isNested !== undefined ? { isNested } : {})}
      {...(repositionInputs !== undefined ? { repositionInputs } : {})}
      {...(showHandle !== undefined ? { showHandle } : {})}
      {...(trigger !== undefined ? { trigger } : {})}
      {...props}
    />
  )
}

type ResponsiveModalRootProps = ComponentProps<typeof BottomDrawerRoot> & ModalRootProps
// Only for bottom drawer
export const ResponsiveModalRoot = ({
  // BottomDrawer specific props
  isNested,
  shouldScaleBackground,
  repositionInputs,
  ...props
}: ResponsiveModalRootProps): React.ReactNode => {
  const isDesktop = useMediaBreakpoint('sm')
  if (isDesktop) {
    return <ModalRoot {...props} />
  }
  return (
    <BottomDrawerRoot
      {...(shouldScaleBackground !== undefined ? { shouldScaleBackground } : {})}
      {...(isNested !== undefined ? { isNested } : {})}
      {...(repositionInputs !== undefined ? { repositionInputs } : {})}
      {...props}
    />
  )
}

export type ResponsiveModalPanelProps = BottomDrawerPanelProps & ModalPanelProps
export const ResponsiveModalPanel = ({
  // Modal specific props
  size,
  // BottomDrawer specific props
  showHandle,
  isNested,
  overlayProps,
  asPortal,
  maxHeight,
  ...props
}: ResponsiveModalPanelProps): React.ReactNode => {
  const isDesktop = useMediaBreakpoint('sm')
  if (isDesktop) {
    return <ModalPanel {...(size !== undefined ? { size } : {})} {...props} />
  }
  return (
    <BottomDrawerPanel
      {...(isNested !== undefined ? { isNested } : {})}
      {...(showHandle !== undefined ? { showHandle } : {})}
      {...(overlayProps !== undefined ? { overlayProps } : {})}
      {...(asPortal !== undefined ? { asPortal } : {})}
      {...(maxHeight !== undefined ? { maxHeight } : {})}
      {...props}
    />
  )
}

export const ResponsiveModalHeader = ({
  children,
  ...props
}: ModalTitleProps & React.ComponentProps<typeof BottomDrawerHeader>): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  const Element = isSM ? ModalHeader : BottomDrawerHeader
  return <Element {...props}>{children}</Element>
}

export const ResponsiveModalHeaderDesktop = ({ children, ...props }: ModalTitleProps): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  if (!isSM) return null
  return <ModalHeader {...props}>{children}</ModalHeader>
}

export const ResponsiveModalHeaderMobile = (
  props: React.ComponentProps<typeof BottomDrawerHeader>
): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  if (isSM) return null
  return <BottomDrawerHeader {...props} />
}

export const ResponsiveModalContent = ({ children, ...props }: ModalContentProps): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  const Element = isSM ? ModalContent : BottomDrawerContent
  return <Element {...props}>{children}</Element>
}

export const ResponsiveModalFooter = ({
  children,
  desktop,
  ...props
}: ModalFooterProps & {
  desktop?: boolean
}): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  const Element = isSM ? ModalFooter : BottomDrawerFooter
  if (desktop && !isSM) return null
  return <Element {...props}>{children}</Element>
}

export const ResponsiveModalFooterDesktop = (props: ModalFooterProps): React.ReactNode => {
  return <ResponsiveModalFooter desktop {...props} />
}

export const ResponsiveModalTrigger = ({
  children,
  asChild
}: {
  children: ReactNode
  asChild?: boolean
}): React.ReactNode => {
  const isDesktop = useMediaBreakpoint('sm')
  const Trigger = isDesktop ? ModalTrigger : BottomDrawerTrigger
  return <Trigger {...(asChild !== undefined ? { asChild } : {})}>{children}</Trigger>
}

export const ResponsiveModalDescription = (
  props: ComponentProps<typeof ModalDescription> & ComponentProps<typeof BottomDrawerDescription>
): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  const Element = isSM ? ModalDescription : BottomDrawerDescription
  return <Element {...props} />
}

export const ResponsiveModalTitle = (
  props: ComponentProps<typeof ModalTitle> & ComponentProps<typeof BottomDrawerTitle>
): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  const Element = isSM ? ModalTitle : BottomDrawerTitle
  return <Element {...props} />
}

export const ResponsiveModalNestedRoot = (
  props: ComponentProps<typeof BottomDrawerNestedRoot> & ComponentProps<typeof ModalRoot>
): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  const Root = isSM ? ModalRoot : BottomDrawerNestedRoot
  return <Root {...props} />
}

export const ResponsiveModalClose = (
  props: ComponentProps<typeof ModalClose> & ComponentProps<typeof BottomDrawerClose>
): React.ReactNode => {
  const isSM = useMediaBreakpoint('sm')
  const Element = isSM ? ModalClose : BottomDrawerClose
  return <Element {...props} />
}
